import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { WorkforceCashAccount, WorkforceEarningLine, WorkforcePerson } from '../../../accounts/workforce-cash/workforce-cash.models';
import { WorkforceCashService } from '../../../accounts/workforce-cash/workforce-cash.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { TenantScopeService } from '../../../company/tenant-scope.service';
import { resolveSisterCompanyScope, sisterCompanyFleetAccessMessage } from '../../../shared/utils/sister-company-scope.util';
import { Driver } from '../driver.model';
import { DriverService } from '../driver.service';
import { FleetOwnershipTransferAudit, FleetOwnershipTransferService } from '../../ownership-transfer/fleet-ownership-transfer.service';
import { FleetOwnershipTransferDialogComponent, OwnershipTransferResource } from '../../ownership-transfer/fleet-ownership-transfer-dialog.component';

@Component({
    selector: 'app-view-driver',
    templateUrl: './view-driver.component.html',
    styleUrls: ['./view-driver.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        ProgressBarModule,
        TagModule,
        TableModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent,
        FleetOwnershipTransferDialogComponent
    ]
})
export class ViewDriverComponent implements OnInit {
    isLoading = true;
    loadError: string | null = null;
    driverId: number;
    driver: Driver = new Driver();
    workforcePerson?: WorkforcePerson;
    workforceCashAccounts: WorkforceCashAccount[] = [];
    workforceEarnings: WorkforceEarningLine[] = [];
    isReadOnlyResource = false;
    ownershipTransferVisible = false;
    ownershipTransferResources: OwnershipTransferResource[] = [];
    ownershipTransferPairedResource: OwnershipTransferResource | null = null;
    ownershipHistory: FleetOwnershipTransferAudit[] = [];
    currentCompanyId: number | null = null;
    private companyGroupId: number | null = null;
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private driverService: DriverService,
        private workforceFinanceService: WorkforceCashService,
        private session: SessionStorageService,
        private tenantScopeService: TenantScopeService,
        private ownershipTransferService: FleetOwnershipTransferService
    ) { }

    ngOnInit(): void {
        this.currentCompanyId = this.session.getCompany()?.id ?? null;
        this.driverId = Number(this.route.snapshot.paramMap.get('id'));
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Drivers', routerLink: '/fleet/drivers' },
            { label: 'View' }
        ];

        if (!this.driverId) {
            this.isLoading = false;
            return;
        }

        this.initializeScopeAndLoad();
    }

    editDriver(): void {
        if (this.isReadOnlyResource) {
            return;
        }
        this.router.navigate(['/fleet/driver/edit', this.driverId], {
            queryParams: this.route.snapshot.queryParams
        });
    }

    backToDrivers(): void {
        this.router.navigate(['/fleet/drivers'], { queryParams: this.listQueryParams() });
    }

    changeOwnership(): void {
        if (!this.currentCompanyId || !this.driverId) return;
        this.ownershipTransferResources = [{ resourceType: 'DRIVER', resourceId: this.driverId, label: this.driver.name || `Driver #${this.driverId}` }];
        const vehicle = this.driver.currentVehicle;
        this.ownershipTransferPairedResource = vehicle?.vehicleId
            ? { resourceType: 'VEHICLE', resourceId: vehicle.vehicleId, label: vehicle.plateNumber || `Vehicle #${vehicle.vehicleId}` }
            : null;
        this.ownershipTransferVisible = true;
    }

    onOwnershipTransferCompleted(targetCompanyId: number): void {
        if (targetCompanyId !== this.currentCompanyId) {
            this.backToDrivers();
            return;
        }
        this.loadDriver();
    }

    driverMetrics(): PageMetric[] {
        return [
            {
                label: 'Phone',
                value: this.driver.phoneNumber || '-',
                hint: this.driver.isPhoneVerified ? 'Verified' : 'Not verified',
                tone: this.driver.isPhoneVerified ? 'success' : 'warn'
            },
            {
                label: 'Iqama Expiry',
                value: this.driver.residenceExpiryDate ? new Date(this.driver.residenceExpiryDate).toLocaleDateString() : '-',
                hint: this.isExpired(this.driver.residenceExpiryDate) ? 'Expired' : 'Active',
                tone: this.isExpired(this.driver.residenceExpiryDate) ? 'danger' : 'success'
            },
            {
                label: 'License Expiry',
                value: this.driver.licenseExpiryDate ? new Date(this.driver.licenseExpiryDate).toLocaleDateString() : '-',
                hint: this.isExpired(this.driver.licenseExpiryDate) ? 'Expired' : 'Active',
                tone: this.isExpired(this.driver.licenseExpiryDate) ? 'danger' : 'success'
            },
            {
                label: 'Current Vehicle',
                value: this.driver.currentVehicle?.plateNumber || '-',
                hint: this.driver.currentVehicle?.registrationNumber || 'No assigned vehicle',
                tone: 'info'
            }
        ];
    }

    isExpired(value?: number): boolean {
        if (!value) {
            return false;
        }
        return value < Date.now();
    }

    documentUrl(url?: string): string | null {
        return url || null;
    }

    formatHomeLocationCoordinates(): string {
        if (!this.driver.homeLocation?.location) {
            return '-';
        }
        const latitude = this.driver.homeLocation.location.latitude;
        const longitude = this.driver.homeLocation.location.longitude;
        if (latitude == null || longitude == null) {
            return '-';
        }
        return `${latitude}, ${longitude}`;
    }

    private loadWorkforceFinance(): void {
        const companyId = this.currentCompanyId;
        if (!companyId || !this.driverId) {
            return;
        }
        this.workforceFinanceService.people(companyId).subscribe({
            next: people => {
                this.workforcePerson = (people || []).find(person => person.driverId === this.driverId);
                const fieldWorkerId = this.workforcePerson?.fieldWorkerId;
                if (!fieldWorkerId) {
                    this.workforceCashAccounts = [];
                    this.workforceEarnings = [];
                    return;
                }
                this.workforceFinanceService.accounts({ owningCompanyId: companyId, fieldWorkerId })
                    .subscribe(accounts => this.workforceCashAccounts = accounts || []);
                this.workforceFinanceService.earnings({ owningCompanyId: companyId, fieldWorkerId })
                    .subscribe(earnings => this.workforceEarnings = earnings || []);
            },
            error: () => {
                this.workforcePerson = undefined;
                this.workforceCashAccounts = [];
                this.workforceEarnings = [];
            }
        });
    }

    private initializeScopeAndLoad(): void {
        const applyScope = () => {
            this.companyGroupId = resolveSisterCompanyScope(
                this.tenantScopeService.getAccessSummary(),
                this.currentCompanyId,
                ['fleet.drivers.view']
            )?.companyGroupId ?? null;
            this.loadDriver();
        };

        this.tenantScopeService.initializeCurrentAccess().subscribe(() => applyScope());
    }

    private loadDriver(): void {
        this.loadError = null;
        this.driverService.findDriverByFilter(
            undefined,
            this.driverId,
            undefined,
            undefined,
            true,
            'en-US',
            this.currentCompanyId ?? undefined,
            this.companyGroupId ?? undefined
        ).subscribe({
            next: response => {
                this.driver = response.data;
                this.isReadOnlyResource = !!this.driver.resourceCompanyId
                    && !!this.currentCompanyId
                    && this.driver.resourceCompanyId !== this.currentCompanyId;
                if (!this.isReadOnlyResource) {
                    this.loadWorkforceFinance();
                }
                this.loadOwnershipHistory();
                this.isLoading = false;
            },
            error: error => {
                this.loadError = error?.status === 403 && this.route.snapshot.queryParamMap.get('resourceView') === 'sister'
                    ? sisterCompanyFleetAccessMessage('driver')
                    : 'Unable to load this driver.';
                this.isLoading = false;
            }
        });
    }

    private loadOwnershipHistory(): void {
        if (!this.currentCompanyId || !this.driverId) return;
        this.ownershipTransferService.history(this.currentCompanyId, 'DRIVER', this.driverId).subscribe({
            next: history => this.ownershipHistory = history || [],
            error: () => this.ownershipHistory = []
        });
    }

    private listQueryParams(): Record<string, string | number> {
        const queryParams: Record<string, string | number> = {};
        const tab = this.route.snapshot.queryParamMap.get('tab');
        const resourceView = this.route.snapshot.queryParamMap.get('resourceView');
        const memberCompanyId = this.route.snapshot.queryParamMap.get('memberCompanyId');
        if (tab) queryParams['tab'] = tab;
        if (resourceView) queryParams['resourceView'] = resourceView;
        if (memberCompanyId) queryParams['memberCompanyId'] = memberCompanyId;
        return queryParams;
    }
}
